import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material'
import { StepProps } from '../../../components/_WMS/Stepper'
import { CameraScanButton } from '../../../components/_WMS/CameraScan'
import { useScanKeyboard } from '../../../hooks/useScanKeyboard'
import { RelocateState, StockKeepingUnitDunInfo } from '../types'
import { RelocateApiService } from '../services/relocateApi'
import { isDunCandidate } from '../utils/dunUtils'
import { Product } from '../../../interfaces/Producto/Product'
import { ApiResponse } from '../../../typings/ApiResponse'
import { ApiConfig } from '../../../config/api'
import axios from 'axios'
import { useProductOrigins } from '../../../hooks'
import { useApi } from '../../../hooks/useApi'
import { getStepperContainerHeight } from '../../../utils/helpers'
import { State } from '../../../components/_EOS/State'
import { Status, State as IState } from '../../../hooks/useFetchState'
import CustomAlert from '../../../components/_WMS/AlertMessage/AlertMessage'

interface SkuAndBrandStepProps extends StepProps<RelocateState> {}

// Los códigos van por `params` y no interpolados: un escaneo con `&`, `+`, `#` o
// espacios rompería la query string al concatenarlo a mano.
const getProductsBySku = (sku: string) => {
  return axios.get<ApiResponse<Product>>(`${ApiConfig.StockKeepingUnitApi}`, {
    params: { sku }
  })
}

const getProductsByEan = (ean: string) => {
  return axios.get<ApiResponse<Product>>(`${ApiConfig.StockKeepingUnitApi}`, {
    params: { ean13: ean }
  })
}

export const SkuAndBrandStep: React.FC<SkuAndBrandStepProps> = ({
  next,
  sharedState,
  setSharedState
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [localSku, setLocalSku] = useState('')
  const [availableProducts, setAvailableProducts] = useState<Product[]>([])
  const [dunInfo, setDunInfo] = useState<StockKeepingUnitDunInfo | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [brandSearchState, setBrandSearchState] = useState<IState>({
    status: Status.Idle
  })
  const [skuError, setSkuError] = useState(false)

  const skuInputRef = useRef<HTMLInputElement>(null)
  // En modo camara el teclado virtual no se levanta salvo que toquen el input.
  const { autoFocus, isCameraCapture } = useScanKeyboard()
  const continueButtonRef = useRef<HTMLButtonElement>(null)

  // Se brinda un pequeño tiempo para la lectura de el produco escaneado.
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([])

  const scheduleTimeout = (fn: () => void, delay: number) => {
    timeoutsRef.current.push(setTimeout(fn, delay))
  }

  const clearScheduledTimeouts = () => {
    timeoutsRef.current.forEach(clearTimeout)
    timeoutsRef.current = []
  }

  // Guard de reentrada: las pistolas de código de barras suelen emitir Enter dos
  // veces, y dos búsquedas en vuelo se pisan el estado y programan dos avances.
  const isSearchingRef = useRef(false)

  useEffect(() => clearScheduledTimeouts, [])

  const {
    productOriginsState: { productOrigins }
  } = useProductOrigins()

  const { callApi } = useApi<ApiResponse<Product>>()
  const { callApi: dunCallApi } = useApi<ApiResponse<StockKeepingUnitDunInfo>>()

  // Focus en SKU al montar el componente.
  // En modo camara no: abriria el teclado virtual.
  useEffect(() => {
    if (autoFocus && skuInputRef.current) {
      skuInputRef.current.focus()
    }
  }, [autoFocus])

  /**
   * Resuelve un código DUN y deja el flujo listo para el paso 2.
   *
   * El DUN trae el SKU y la marca, pero no si el producto maneja lote o
   * vencimiento, y FEFO lo necesita: por eso se recupera igual el producto y se
   * elige el de la marca del DUN (un mismo SKU tiene un registro por marca).
   *
   * Devuelve true si el código era un DUN — resuelto o con error propio — para que
   * el llamador no lo reintente como SKU/EAN.
   */
  const resolveDunCode = async (dunValue: string): Promise<boolean> => {
    let dunPage: ApiResponse<StockKeepingUnitDunInfo> | undefined
    try {
      dunPage = await dunCallApi(RelocateApiService.resolveDun(dunValue))
    } catch (error) {
      // El código puede no ser un DUN: se sigue con la búsqueda por sku/ean13.
      return false
    }

    const dun = dunPage?.items?.[0]
    if (!dun) {
      return false
    }

    // Sin unidades contenidas no se puede convertir cajas a unidades: dejarlo pasar
    // llevaría al operador a un tope de 0 cajas con un mensaje incomprensible.
    if (!dun.quantity || dun.quantity <= 0) {
      setAvailableProducts([])
      setBrandSearchState({
        status: Status.Error,
        error: `El DUN ${dun.dun} no tiene unidades contenidas configuradas: revisar el catálogo de DUN del producto`
      })
      setSkuError(true)
      return true
    }

    // Datos del DUN propiamente dicho: valen igual se resuelva o no la marca.
    const dunState = {
      isDun: true,
      dunCode: dun.dun,
      dunQty: dun.quantity,
      stockKeepingUnitDunId: dun.stockKeepingUnitDunId,
      typePackageName: dun.typePackageName || 'Caja',
      cantidadDun: null
    }

    const products = await callApi(getProductsBySku(dun.sku))
    const productItems: Product[] = products?.items ?? []

    if (productItems.length === 0) {
      setAvailableProducts([])
      setBrandSearchState({
        status: Status.Error,
        error: `El DUN ${dun.dun} apunta al SKU ${dun.sku}, que no existe en el catálogo de productos`
      })
      setSkuError(true)
      return true
    }

    const product = dun.productOriginId
      ? productItems.find((item) => item.productOriginId === dun.productOriginId)
      : undefined

    setDunInfo(dun)

    // El DUN cuelga de un StockKeepingUnit, así que normalmente ya trae la marca
    // resuelta. Si el backend no la informa, se cae al flujo normal de selección de
    // marca en lugar de bloquear al operador: el modo DUN se conserva igual.
    if (!product) {
      setAvailableProducts(productItems)
      setSharedState((prev) => ({ ...prev, ...dunState, sku: dun.sku }))
      setBrandSearchState({ status: Status.Success, data: { showDropdown: true } })
      return true
    }

    const selectedBrand = productOrigins?.find(
      (brand) => brand.productOriginId === dun.productOriginId
    )

    setAvailableProducts([product])
    setSharedState((prev) => ({
      ...prev,
      ...dunState,
      sku: dun.sku,
      productOriginId: dun.productOriginId,
      productOriginName: selectedBrand?.name || '',
      isGreenSale: product.isGreenSale || false,
      hasBatchManagement: product.batch || false,
      hasExpirationDate: product.expirationDate || false,
      expirationDays: selectedBrand?.expirationDays || 0
    }))
    setBrandSearchState({ status: Status.Success })

    // Pausa para que el operador alcance a leer SKU, unidades equivalentes y
    // unidad de medida antes de que cambie la pantalla.
    scheduleTimeout(() => next(), 1600)

    return true
  }

  /**
   * Acepta el valor por parametro para el escaneo por camara: setLocalSku es
   * asincrono, asi que leer del estado en el mismo tick usaria el valor anterior.
   * Sin argumento se comporta igual que antes (Enter y boton Buscar).
   */
  const handleSkuSearch = async (scannedValue?: string) => {
    const skuValue = (scannedValue ?? localSku).trim()
    if (!skuValue) {
      setBrandSearchState({
        status: Status.Error,
        error: 'Código SKU/EAN/DUN no reconocido'
      })
      setSkuError(true)
      return
    }

    // Una búsqueda nueva invalida cualquier avance de paso pendiente del código anterior.
    clearScheduledTimeouts()

    if (isSearchingRef.current) {
      return
    }
    isSearchingRef.current = true

    setIsLoading(true)
    setBrandSearchState({ status: Status.Idle })
    setSkuError(false)
    setDunInfo(null)

    // Reset all data from step 2 when searching new SKU
    setSharedState((prev) => ({
      ...prev,
      productOriginId: null,
      productOriginName: '',
      posicionOrigen: '',
      cantidad: null,
      posicionDestino: '',
      availableStock: undefined,
      originPositionId: undefined,
      destinyPositionId: undefined,
      // El modo DUN no puede sobrevivir a una búsqueda nueva: si quedara activo,
      // la cantidad del próximo producto se multiplicaría por la caja anterior.
      isDun: false,
      dunCode: undefined,
      dunQty: undefined,
      stockKeepingUnitDunId: undefined,
      typePackageName: undefined,
      cantidadDun: null
    }))

    try {
      let items: Product[] = (await callApi(getProductsBySku(skuValue)))?.items ?? []

      if (items.length === 0) {
        items = (await callApi(getProductsByEan(skuValue)))?.items ?? []
      }
      if (items.length === 0) {        
        if (isDunCandidate(skuValue) && (await resolveDunCode(skuValue))) {
          return
        }

        setAvailableProducts([])
        setBrandSearchState({
          status: Status.Error,
          error: 'No se encontraron productos para este código SKU/EAN/DUN'
        })
        setSkuError(true)
      } else {
        setAvailableProducts(items)
        setSharedState((prev) => ({ ...prev, sku: skuValue }))

        // Mostrar loading mientras se procesan las marcas
        setBrandSearchState({ status: Status.Loading })

        scheduleTimeout(() => {
          if (items.length === 1) {
            // Auto-seleccionar si solo hay una marca disponible
            const singleProduct = items[0]
            const selectedBrand = productOrigins?.find(
              (brand) => brand.productOriginId === singleProduct.productOriginId
            )

            setSharedState((prev) => ({
              ...prev,
              productOriginId: singleProduct.productOriginId ?? null,
              productOriginName: selectedBrand?.name || '',
              isGreenSale: singleProduct.isGreenSale || false,
              hasBatchManagement: singleProduct.batch || false,
              hasExpirationDate: singleProduct.expirationDate || false,
              expirationDays: selectedBrand?.expirationDays || 0
            }))

            // Avanzar automáticamente después de un breve delay
            scheduleTimeout(() => {
              setBrandSearchState({ status: Status.Success })
              next()
            }, 1200)
          } else {
            // Múltiples marcas: mostrar dropdown
            setBrandSearchState({
              status: Status.Success,
              data: { showDropdown: true }
            })
          }
        }, 800)
      }
    } catch (err) {
      setAvailableProducts([])
      setBrandSearchState({
        status: Status.Error,
        error: 'Error al buscar el código SKU/EAN/DUN'
      })
      setSkuError(true)
    } finally {
      isSearchingRef.current = false
      setIsLoading(false)
    }
  }

  const handleSkuChange = (value: string) => {
    setLocalSku(value)

    // Si el operador empieza a tipear otro código, se cancela el avance pendiente y
    // se baja la tarjeta del DUN anterior: no debe irse de paso con lo que ya cambió.
    clearScheduledTimeouts()
    if (dunInfo) {
      setDunInfo(null)
    }

    // Reset error state when user starts typing
    if (skuError) {
      setSkuError(false)
      setBrandSearchState({ status: Status.Idle })
    }
  }

  const handleBrandSelect = (productOriginId: number) => {
    const selectedBrand = productOrigins?.find(
      (brand) => brand.productOriginId === productOriginId
    )

    const selectedProduct = availableProducts.find(
      (product) => product.productOriginId === productOriginId
    )

    setSharedState((prev) => ({
      ...prev,
      productOriginId,
      productOriginName: selectedBrand?.name || '',      
      isGreenSale: selectedProduct?.isGreenSale || false,
      hasBatchManagement: selectedProduct?.batch || false,
      hasExpirationDate: selectedProduct?.expirationDate || false,
      expirationDays: selectedBrand?.expirationDays || 0
    }))

    // Focus en el botón Continuar después de seleccionar marca
    setTimeout(() => {
      if (continueButtonRef.current) {
        continueButtonRef.current.focus()
      }
    }, 100)
  }

  const handleNext = () => {
    if (!sharedState.sku || !sharedState.productOriginId) {
      setBrandSearchState({
        status: Status.Error,
        error: 'Complete todos los campos antes de continuar'
      })
      return
    }

    // Reset position and quantity data when proceeding to next step
    setSharedState((prev) => ({
      ...prev,
      posicionOrigen: '',
      cantidad: null,      
      cantidadDun: null,
      posicionDestino: '',
      availableStock: undefined,
      originPositionId: undefined,
      destinyPositionId: undefined
    }))

    next()
  }

  const isContinueVisible =
    availableProducts.length > 0 &&
    brandSearchState.status === Status.Success &&
    brandSearchState.data?.showDropdown

  const canProceed =
    sharedState.sku &&
    sharedState.productOriginId &&
    availableProducts.length > 0 &&
    brandSearchState.status === Status.Success &&
    brandSearchState.data?.showDropdown

  return (
    <Box
      style={{
        height: isMobile ? '100%' : getStepperContainerHeight(),
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <Box p={3} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant='h5' sx={{ mb: 1, textAlign: 'center' }}>
          Seleccionar Producto
        </Typography>

        {/* SKU Input */}
        <Box
          mb={1}
          mt={isMobile ? 1 : 8}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 'none',
            flexDirection: 'column'
          }}
        >
          <Box
            display='flex'
            alignItems='flex-start'
            // En modo camara siempre columna: el boton de camara va entre el
            // input y "Buscar", y en fila los tres no entrarian.
            flexDirection={isMobile || isCameraCapture ? 'column' : 'row'}
            style={{ width: '100%', maxWidth: '600px' }}
          >
            <TextField
              inputRef={skuInputRef}
              label='SKU/EAN/DUN'
              variant='outlined'
              fullWidth
              value={localSku}
              onChange={(e) => handleSkuChange(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSkuSearch()
                }
              }}
              error={skuError}
              disabled={isLoading || brandSearchState.status === Status.Loading}
              sx={{
                marginRight: isMobile ? 0 : '16px',
                ...(availableProducts.length > 0 &&
                  !skuError && {
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: theme.palette.success.main,
                        borderWidth: '2px'
                      }
                    }
                  })
              }}
            />
            {/* Entre el input y "Buscar": el boton de camara es una alternativa
                para llenar el campo, no una accion sobre lo ya ingresado. */}
            <Box style={{ width: '100%', marginTop: 12 }}>
              <CameraScanButton
                inputRef={skuInputRef}
                onScan={(value) => {
                  // handleSkuChange y no setLocalSku: ademas limpia el error previo.
                  handleSkuChange(value)
                  handleSkuSearch(value)
                }}
                onCancel={() => autoFocus && setTimeout(() => skuInputRef.current?.focus(), 0)}
                disabled={isLoading || brandSearchState.status === Status.Loading}
                dialogTitle='Escanear SKU, EAN o DUN'
                helperText='Centra el código del producto dentro del recuadro'
              />
            </Box>

            {!(
              brandSearchState.status === Status.Success &&
              brandSearchState.data?.showDropdown
            ) && (
              <Button
                variant='contained'
                color='primary'
                // Envuelto a proposito: pasarlo directo mandaria el MouseEvent
                // como scannedValue.
                onClick={() => handleSkuSearch()}
                disabled={isLoading || !localSku.trim()}
                fullWidth={isMobile || isCameraCapture}
                style={{
                  minWidth: isMobile ? 'auto' : '100px',
                  height: '56px',
                  marginTop: isMobile || isCameraCapture ? 12 : 0
                }}
              >
                {isLoading ? 'Buscando...' : 'Buscar'}
              </Button>
            )}
          </Box>

          {/* SKU Error Message */}
          {skuError && brandSearchState.status === Status.Error && (
            <CustomAlert
              severity='error'
              message={brandSearchState.error}
              maxWidth={600}
            />
          )}

          {/* Confirmación de lectura del DUN: SKU, unidades equivalentes y unidad
              de medida antes de avanzar al paso de posiciones. */}
          {dunInfo && (
            <Box mt={3} style={{ width: '100%', maxWidth: '600px' }}>
              <Card
                variant='outlined'
                style={{ borderColor: theme.palette.success.main, borderWidth: 2 }}
              >
                <CardContent>
                  <Typography variant='caption' color='textSecondary'>
                    DUN reconocido
                  </Typography>
                  <Typography variant='h6' style={{ fontWeight: 600 }}>
                    {dunInfo.dun}
                  </Typography>

                  <Divider style={{ margin: '12px 0' }} />

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                      label={`SKU: ${dunInfo.sku}`}
                      color='primary'
                      variant='outlined'
                      size='small'
                    />
                    <Chip
                      label={`Unidad de medida: ${dunInfo.typePackageName}`}
                      variant='outlined'
                      size='small'
                    />
                    <Chip
                      label={`Unidades contenidas: ${dunInfo.quantity}`}
                      color='primary'
                      size='small'
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Brand Search State */}
          {availableProducts.length > 0 &&
            (!dunInfo || brandSearchState.data?.showDropdown) && (
            <Box mt={3} style={{ width: '100%', maxWidth: '600px' }}>
              <State
                state={brandSearchState}
                loadingMessage='Consultando marcas disponibles...'
              >
                {brandSearchState.data?.showDropdown && (
                  <FormControl variant='outlined' fullWidth>
                    <InputLabel>Marca</InputLabel>
                    <Select
                      value={sharedState.productOriginId || ''}
                      onChange={(e) =>
                        handleBrandSelect(Number(e.target.value))
                      }
                      label='Marca'
                      disabled={brandSearchState.status === Status.Loading}
                    >
                      {availableProducts.map((product) => {
                        const brand = productOrigins?.find(
                          (b) => b.productOriginId === product.productOriginId
                        )
                        return (
                          <MenuItem
                            key={product.productOriginId}
                            value={product.productOriginId}
                          >
                            {brand?.name || `ID: ${product.productOriginId}`}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </FormControl>
                )}
              </State>
            </Box>
          )}
        </Box>

        {/* Next Button */}
        {isContinueVisible && (
            <Box display='flex' justifyContent='flex-end'>
              <Button
                ref={continueButtonRef}
                variant='contained'
                color='primary'
                onClick={handleNext}
                disabled={!canProceed}
                size='large'
                style={{
                  backgroundColor: canProceed
                    ? theme.palette.success.main
                    : undefined,
                  color: canProceed ? '#fff' : undefined
                }}
              >
                Continuar
              </Button>
            </Box>
          )}
      </Box>
    </Box>
  )
}
